import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Container, Grid, Slide } from '@mui/material';

import { useAdminRulesApi } from './hooks/admin';
import { useDeleteAdminRuleMutation, useGetAdminRuleQuery } from './store/adminApi';
import {
  AdminRulesHeader,
  DeleteRuleDialog,
  RuleDetailPanel,
  RuleFormPanel,
  RulesList,
} from './components';
import type { ProviderRuleResponse, RuleScope, RuleType } from './types';

/**
 * Admin Rules Management Page
 *
 * Admin-only page for managing global and organization-scoped rules.
 * Features:
 * - Organization context switching (Global vs specific org)
 * - Full CRUD for global rules when in global context
 * - View/manage org-specific rules when org is selected
 * - Split-pane layout for browsing and details
 */
export const AdminRulesManagementPage: React.FC = () => {
  // Organization context state
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [selectedOrgName, setSelectedOrgName] = useState<string | null>(null);

  // Filters and pagination state
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<RuleScope>('both');
  const [ruleType, setRuleType] = useState<RuleType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Selection state for detail panel
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);

  // Panel mode: 'view' | 'create' | 'edit'
  const [panelMode, setPanelMode] = useState<'view' | 'create' | 'edit'>('view');

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<ProviderRuleResponse | null>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset state when organization context changes
  useEffect(() => {
    setSelectedRuleId(null);
    setPanelMode('view');
    setPage(1);
    setSearchQuery('');
    setDebouncedSearchQuery('');
  }, [selectedOrgId]);

  // Use the admin rules API hook
  const { rules, totalPages, isLoading } = useAdminRulesApi({
    organizationId: selectedOrgId,
    page,
    perPage: 20,
    scope,
    ruleType,
    searchQuery: debouncedSearchQuery,
  });

  // Fetch selected rule data
  const { data: selectedRule } = useGetAdminRuleQuery(selectedRuleId ?? '', {
    skip: !selectedRuleId,
  });

  // Delete mutation
  const [deleteRule, { isLoading: isDeleting }] = useDeleteAdminRuleMutation();

  // Handlers
  const handleOrgChange = useCallback((orgId: string | null, orgName: string | null) => {
    setSelectedOrgId(orgId);
    setSelectedOrgName(orgName);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleScopeChange = useCallback((newScope: RuleScope) => {
    setScope(newScope);
    setPage(1);
  }, []);

  const handleRuleTypeChange = useCallback((newType: RuleType | 'all') => {
    setRuleType(newType);
    setPage(1);
  }, []);

  const handleAddRule = useCallback(() => {
    setSelectedRuleId(null);
    setPanelMode('create');
  }, []);

  const handleRuleClick = useCallback((rule: ProviderRuleResponse) => {
    setSelectedRuleId(rule.rule_id);
    setPanelMode('view');
  }, []);

  const handlePanelClose = useCallback(() => {
    setSelectedRuleId(null);
    setPanelMode('view');
  }, []);

  const handleEditRule = useCallback(() => {
    if (selectedRuleId) {
      setPanelMode('edit');
    }
  }, [selectedRuleId]);

  const handleDeleteRule = useCallback(() => {
    if (selectedRule) {
      // selectedRule is already in ProviderRuleResponse format from admin API
      setRuleToDelete(selectedRule as ProviderRuleResponse);
      setDeleteDialogOpen(true);
    }
  }, [selectedRule]);

  const handleConfirmDelete = useCallback(async () => {
    if (!ruleToDelete) return;

    try {
      await deleteRule(ruleToDelete.rule_id).unwrap();
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
      setSelectedRuleId(null);
      setPanelMode('view');
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  }, [ruleToDelete, deleteRule]);

  const handleDeleteDialogClose = useCallback(() => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  }, [isDeleting]);

  const handleFormSuccess = useCallback(() => {
    if (panelMode === 'create') {
      setSelectedRuleId(null);
      setPanelMode('view');
    } else if (panelMode === 'edit' && selectedRuleId) {
      setPanelMode('view');
    }
  }, [panelMode, selectedRuleId]);

  // selectedRule is already in ProviderRuleResponse format from admin API
  const selectedRuleForPanel: ProviderRuleResponse | null = selectedRule
    ? (selectedRule as ProviderRuleResponse)
    : null;

  return (
    <Container
      data-testid="admin-rules-workspace"
      sx={{py: 4, height: '100%', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}
     maxWidth={false}>
      <AdminRulesHeader
        selectedOrgId={selectedOrgId}
        selectedOrgName={selectedOrgName}
        onOrgChange={handleOrgChange}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        scope={scope}
        onScopeChange={handleScopeChange}
        ruleType={ruleType}
        onRuleTypeChange={handleRuleTypeChange}
        onAddRule={handleAddRule}
      />

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        {/* List Pane */}
        <Grid
          data-testid="admin-rules-list"
          size={{ xs: 12, md: selectedRuleId || panelMode === 'create' || panelMode === 'edit' ? 5 : 12, lg: selectedRuleId || panelMode === 'create' || panelMode === 'edit' ? 4 : 12 }}
          sx={{
            height: '100%',
            minHeight: 0,
            minWidth: 0,
            overflowY: 'auto',
            transition: 'all 0.3s ease',
          }}
        >
          <RulesList
            rules={rules as ProviderRuleResponse[]}
            isLoading={isLoading}
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onRuleClick={handleRuleClick}
          />
        </Grid>

        {/* Detail/Form Pane */}
        {(selectedRuleId || panelMode === 'create' || panelMode === 'edit') && (
          <Grid data-testid="admin-rule-detail" size={{ xs: 12, md: 7, lg: 8 }} sx={{ height: '100%', minHeight: 0, minWidth: 0, overflowY: 'auto' }}>
            <Slide direction="left" in={true} mountOnEnter unmountOnExit>
              <Box sx={{ minHeight: '100%' }}>
                {panelMode === 'view' && selectedRuleId ? (
                  selectedRuleForPanel ? (
                    <RuleDetailPanel
                      fillHeight={false}
                      rule={selectedRuleForPanel}
                      onClose={handlePanelClose}
                      onEdit={handleEditRule}
                      onDelete={handleDeleteRule}
                      isAdmin
                    />
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )
                ) : panelMode === 'create' || panelMode === 'edit' ? (
                  <RuleFormPanel
                    mode={panelMode}
                    rule={selectedRuleForPanel}
                    onClose={handlePanelClose}
                    onSuccess={handleFormSuccess}
                    isAdmin
                    organizationId={selectedOrgId}
                  />
                ) : null}
              </Box>
            </Slide>
          </Grid>
        )}
      </Grid>

      {/* Delete Confirmation Dialog */}
      <DeleteRuleDialog
        open={deleteDialogOpen}
        rule={ruleToDelete}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </Container>
  );
};

export default AdminRulesManagementPage;

